import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building, Calendar, Code, Check, X, Briefcase, AlertCircle, User, LogOut, ChevronDown, Clock, MessageCircle } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { useToast } from '../components/Admissionform/ToastManager';
import '../styles/CompanyNotifications.css';

const CompanyNotifications = () => {
    const navigate = useNavigate();
    const { showSuccess, showError } = useToast();
    const [notifications, setNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [processingIds, setProcessingIds] = useState(new Set());
    const [rejectReason, setRejectReason] = useState('');
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [studentData, setStudentData] = useState(null);
    const [showProfileDropdown, setShowProfileDropdown] = useState(false);
    const [showAttendanceModal, setShowAttendanceModal] = useState(false);
    const [selectedAttendanceNotification, setSelectedAttendanceNotification] = useState(null);
    const [attendanceStatus, setAttendanceStatus] = useState('');
    const [feedback, setFeedback] = useState('');
    const dropdownRef = useRef(null);

    useEffect(() => {
        // Get student data from localStorage
        const storedData = localStorage.getItem('studentData');
        if (storedData) {
            const parsedData = JSON.parse(storedData);
            setStudentData(parsedData);
            fetchNotifications(parsedData.studentId);
        } else {
            showError('Student data not found. Please login again.');
            navigate('/');
        }
    }, [navigate]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowProfileDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const fetchNotifications = async (studentId) => {
        try {
            setIsLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/student-notifications/${studentId}`);

            if (response.data.success) {
                setNotifications(response.data.notifications || []);
            } else {
                throw new Error(response.data.message || 'Failed to fetch notifications');
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
            showError(error.response?.data?.message || 'Failed to fetch company notifications');
        } finally {
            setIsLoading(false);
        }
    };

    const handleAccept = async (notificationId) => {
        if (processingIds.has(notificationId)) return;

        try {
            setProcessingIds(prev => new Set(prev).add(notificationId));

            const response = await axios.put(`${process.env.REACT_APP_API_URL}/company-response/${notificationId}`, {
                studentId: studentData.studentId,
                isAccepted: true,
                rejectReason: null
            });

            if (response.data.success) {
                showSuccess('Company offer accepted successfully!');
                // Refresh notifications
                fetchNotifications(studentData.studentId);
            } else {
                throw new Error(response.data.message || 'Failed to accept offer');
            }
        } catch (error) {
            console.error('Error accepting offer:', error);
            showError(error.response?.data?.message || 'Failed to accept company offer');
        } finally {
            setProcessingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(notificationId);
                return newSet;
            });
        }
    };

    const handleReject = (notification) => {
        setSelectedNotification(notification);
        setRejectReason('');
        setShowRejectModal(true);
    };

    const confirmReject = async () => {
        if (!rejectReason.trim()) {
            showError('Please provide a reason for rejection');
            return;
        }

        if (processingIds.has(selectedNotification.id)) return;

        try {
            setProcessingIds(prev => new Set(prev).add(selectedNotification.id));

            const response = await axios.put(`${process.env.REACT_APP_API_URL}/company-response/${selectedNotification.id}`, {
                studentId: studentData.studentId,
                isAccepted: false,
                rejectReason: rejectReason.trim()
            });

            if (response.data.success) {
                showSuccess('Company offer rejected successfully');
                // Refresh notifications
                fetchNotifications(studentData.studentId);
                setShowRejectModal(false);
                setSelectedNotification(null);
                setRejectReason('');
            } else {
                throw new Error(response.data.message || 'Failed to reject offer');
            }
        } catch (error) {
            console.error('Error rejecting offer:', error);
            showError(error.response?.data?.message || 'Failed to reject company offer');
        } finally {
            setProcessingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(selectedNotification.id);
                return newSet;
            });
        }
    };

    const handleAttendanceUpdate = (notification) => {
        setSelectedAttendanceNotification(notification);
        setAttendanceStatus('');
        setFeedback('');
        setShowAttendanceModal(true);
    };

    const submitAttendanceFeedback = async () => {
        if (!attendanceStatus) {
            showError('Please select attendance status');
            return;
        }

        if (!feedback.trim()) {
            showError('Please provide feedback');
            return;
        }

        if (processingIds.has(selectedAttendanceNotification.id)) return;

        try {
            setProcessingIds(prev => new Set(prev).add(selectedAttendanceNotification.id));

            const response = await axios.put(`${process.env.REACT_APP_API_URL}/interview-attendance/${selectedAttendanceNotification.id}`, {
                studentId: studentData.studentId,
                attended: attendanceStatus === 'attended',
                feedback: feedback.trim()
            });

            if (response.data.success) {
                showSuccess('Interview attendance updated successfully!');
                fetchNotifications(studentData.studentId);
                setShowAttendanceModal(false);
                setSelectedAttendanceNotification(null);
                setAttendanceStatus('');
                setFeedback('');
            } else {
                throw new Error(response.data.message || 'Failed to update attendance');
            }
        } catch (error) {
            console.error('Error updating attendance:', error);
            showError(error.response?.data?.message || 'Failed to update interview attendance');
        } finally {
            setProcessingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(selectedAttendanceNotification.id);
                return newSet;
            });
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not specified';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getProfileImageUrl = (profilePicPath) => {
        if (!profilePicPath) return null;
        const cleanPath = profilePicPath.split('?')[0];
        return `https://pumotechnovationcrm.com${cleanPath}?t=${Date.now()}`;
    };

    const handleMyProfile = () => {
        setShowProfileDropdown(false);
        navigate('/stu-dashboard');
    };

    const handleLogout = () => {
        setShowProfileDropdown(false);
        localStorage.removeItem('studentData');
        localStorage.removeItem('studentToken');
        setStudentData(null);
        navigate('/');
    };

    // Get comprehensive status for a notification
    const getNotificationStatus = (notification) => {
        // If offer was rejected
        if (notification.isAccepted === false) {
            return {
                type: 'rejected',
                text: 'Rejected',
                icon: <X size={12} />,
                reason: notification.rejectReason
            };
        }

        // If offer was accepted
        if (notification.isAccepted === true) {
            // Attendance already marked
            if (notification.attended !== null) {
                if (notification.attended) {
                    return {
                        type: 'attended',
                        text: 'Attended',
                        icon: <Check size={12} />,
                        feedback: notification.feedback
                    };
                } else {
                    return {
                        type: 'not-attended',
                        text: 'Not Attended',
                        icon: <X size={12} />,
                        feedback: notification.feedback
                    };
                }
            }

            // ✅ Pending attendance if interview date is today or past
            if (notification.interviewDate) {
                const interview = new Date(notification.interviewDate).setHours(0, 0, 0, 0);
                const today = new Date().setHours(0, 0, 0, 0);
                if (interview <= today) {
                    return {
                        type: 'pending-attendance',
                        text: 'Pending Attendance',
                        icon: <Clock size={12} />
                    };
                }
            }

            // Accepted but interview date is in future
            return {
                type: 'accepted-waiting',
                text: 'Accepted - Waiting for Interview',
                icon: <Calendar size={12} />
            };
        }

        // Pending response
        return {
            type: 'pending',
            text: 'Pending Response',
            icon: <Clock size={12} />
        };
    };

    // Filter notifications that need response (pending)
    const pendingNotifications = notifications.filter(n => n.isAccepted === null);

    // Accepted notifications that need attendance tracking (interview date is today)
    // Notifications that still need attendance (accepted but not yet marked)
    const pendingAttendanceNotifications = notifications.filter(n => {
        if (n.isAccepted !== true || n.attended !== null) return false;
        if (!n.interviewDate) return false;

        const interview = new Date(n.interviewDate).setHours(0, 0, 0, 0);
        const today = new Date().setHours(0, 0, 0, 0);

        return interview <= today; // only today or past
    });



    // All responded notifications for the table
    const respondedNotifications = notifications.filter(n => n.isAccepted !== null);

    if (isLoading) {
        return (
            <div className="notifications-container">
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Loading company notifications...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="notifications-container">
            {/* Header with center title and right-aligned profile dropdown */}
            <div className="notifications-header">
                <div className="header-content">
                    <h1>Company Notifications</h1>
                </div>

                {/* Profile dropdown in top right corner */}
                <div className="profile-dropdown-container" ref={dropdownRef}>
                    <button
                        className="profile-dropdown-trigger"
                        onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                    >
                        <div className="profile-avatar-small">
                            {studentData?.profilePicPath ? (
                                <img
                                    src={getProfileImageUrl(studentData.profilePicPath)}
                                    alt="Profile"
                                    className="avatar-image"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'block';
                                    }}
                                />
                            ) : (
                                <FontAwesomeIcon icon={faUser} className="avatar-icon" />
                            )}
                            {studentData?.profilePicPath && (
                                <FontAwesomeIcon
                                    icon={faUser}
                                    className="avatar-icon"
                                    style={{ display: 'none' }}
                                />
                            )}
                        </div>
                        <ChevronDown size={16} className={`dropdowncn-arrow ${showProfileDropdown ? 'rotated' : ''}`} />
                    </button>

                    {showProfileDropdown && (
                        <div className="profile-dropdown-menu">
                            <button className="dropdown-item" onClick={handleMyProfile}>
                                <User size={16} />
                                My Profile
                            </button>
                            <button className="dropdown-item logout-item" onClick={handleLogout}>
                                <LogOut size={16} />
                                Logout
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Today's Interview Attendance Section */}
            {pendingAttendanceNotifications.length > 0 && (
                <div className="attendance-section">
                    <h2>Interviews - Mark Attendance</h2>
                    <div className="notifications-list">
                        {pendingAttendanceNotifications.map((notification) => (
                            <div key={`attendance-${notification.id}`} className="notification-card attendance-card">
                                {/* Company Name Header */}
                                <div className="company-name-header">
                                    <Clock size={28} />
                                    <h3>
                                        {notification.companyName} - {formatDate(notification.interviewDate)}
                                    </h3>
                                </div>

                                {/* Company Details Grid */}
                                <div className="company-details-grid">
                                    <div className="detail-item">
                                        <Code size={20} />
                                        <div>
                                            <span className="detail-label">Technology: </span>
                                            <span className="detail-value">{notification.technology || 'Not specified'}</span>
                                        </div>
                                    </div>

                                    <div className="detail-item">
                                        <Calendar size={20} />
                                        <div>
                                            <span className="detail-label">Interview Date: </span>
                                            <span className="detail-value">{formatDate(notification.interviewDate)}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Attendance Action Buttons */}
                                <div className="attendance-actions">
                                    <button
                                        className="attended-button"
                                        onClick={() => {
                                            setAttendanceStatus('attended');
                                            handleAttendanceUpdate(notification);
                                        }}
                                        disabled={processingIds.has(notification.id)}
                                    >
                                        <Check size={20} />
                                        Attended
                                    </button>

                                    <button
                                        className="not-attended-button"
                                        onClick={() => {
                                            setAttendanceStatus('not-attended');
                                            handleAttendanceUpdate(notification);
                                        }}
                                        disabled={processingIds.has(notification.id)}
                                    >
                                        <X size={20} />
                                        Not Attended
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Pending Notifications Cards */}
            {pendingNotifications.length === 0 && pendingAttendanceNotifications.length === 0 ? (
                <div className="no-notifications">
                    <AlertCircle size={48} />
                    <h3>No Pending Notifications</h3>
                    <p>You don't have any company placement notifications requiring response or attendance marking.</p>
                </div>
            ) : (
                <div className="notifications-section">
                    <h2>Pending Company Offers</h2>
                    <div className="notifications-list">
                        {pendingNotifications.map((notification) => (
                            <div key={notification.id} className="notification-card">
                                {/* Company Name Header */}
                                <div className="company-name-header">
                                    <Building size={28} />
                                    <h3>{notification.companyName}</h3>
                                </div>

                                {/* Company Details Grid */}
                                <div className="company-details-grid">
                                    <div className="detail-item">
                                        <Code size={20} />
                                        <div>
                                            <span className="detail-label">Technology: </span>
                                            <span className="detail-value">
                                                {notification.technology || notification.skillSet || 'Not specified'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="detail-item">
                                        <Calendar size={20} />
                                        <div>
                                            <span className="detail-label">Interview Date: </span>
                                            <span className="detail-value">
                                                {formatDate(notification.interviewDate || notification.dateOfPlacement)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Job Description */}
                                {notification.recruitmentRole && (
                                    <div className="job-description">
                                        <div className="detail-item">
                                            <Briefcase size={20} />
                                            <div>
                                                <span className="detail-label">Job Description: </span>
                                                <p className="job-desc-text">{notification.recruitmentRole}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Action Buttons */}
                                <div className="notification-actions">
                                    <button
                                        className="accept-button"
                                        onClick={() => handleAccept(notification.id)}
                                        disabled={processingIds.has(notification.id)}
                                    >
                                        <Check size={20} />
                                        {processingIds.has(notification.id) ? 'Accepting...' : 'Accept'}
                                    </button>

                                    <button
                                        className="reject-button"
                                        onClick={() => handleReject(notification)}
                                        disabled={processingIds.has(notification.id)}
                                    >
                                        <X size={20} />
                                        Reject
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}


            {/* Response History Table */}
            {respondedNotifications.length > 0 && (
                <div className="response-history">
                    <h2>Response History</h2>
                    <div className="table-container">
                        <table className="response-table">
                            <thead>
                                <tr>
                                    <th>Company Name</th>
                                    <th>Interview Date</th>
                                    <th>Technology</th>
                                    <th>Job Description</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {respondedNotifications.map((notification) => {
                                    const status = getNotificationStatus(notification);
                                    return (
                                        <tr key={notification.id}>
                                            <td>
                                                <div className="company-cell">
                                                    <Building size={16} />
                                                    <span>{notification.companyName}</span>
                                                </div>
                                            </td>
                                            <td>{formatDate(notification.interviewDate || notification.dateOfPlacement)}</td>
                                            <td>{notification.technology || notification.skillSet || 'Not specified'}</td>
                                            <td>
                                                <div className="job-desc-cell" title={notification.recruitmentRole}>
                                                    {notification.recruitmentRole || 'Not specified'}
                                                </div>
                                            </td>
                                            <td>
                                                <div className="status-cell">
                                                    <span className={`status-badge ${status.type}`}>
                                                        {status.icon}
                                                        {status.text}
                                                    </span>

                                                    {/* Show rejection reason if rejected */}
                                                    {status.type === 'rejected' && status.reason && (
                                                        <div className="status-details">
                                                            <span className="detail-label">Reason: </span>
                                                            <span className="detail-text">{status.reason}</span>
                                                        </div>
                                                    )}

                                                    {/* Show feedback if provided */}
                                                    {(status.type === 'attended' || status.type === 'not-attended') && status.feedback && (
                                                        <div className="status-details">
                                                            <MessageCircle size={12} />
                                                            <span className="detail-text" title={status.feedback}>
                                                                Feedback: {status.feedback.length > 50 ?
                                                                    `${status.feedback.substring(0, 50)}...` :
                                                                    status.feedback
                                                                }
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Reject Modal */}
            {showRejectModal && (
                <div className="modal-overlay">
                    <div className="reject-modal">
                        <div className="modalcn-header">
                            <h3>Reject Company Offer</h3>
                            <button
                                className="close-button"
                                onClick={() => setShowRejectModal(false)}
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="modalcn-content">
                            <p>You are about to reject the offer from <strong>{selectedNotification?.companyName}</strong>.</p>
                            <p>Please provide a reason for your rejection:</p>
                            <textarea
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                placeholder="Enter your reason for rejecting this offer..."
                                rows={4}
                                className="reject-textarea"
                            />
                        </div>

                        <div className="modalcn-actions">
                            <button
                                className="cancel-button"
                                onClick={() => setShowRejectModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="confirm-reject-button"
                                onClick={confirmReject}
                                disabled={!rejectReason.trim() || processingIds.has(selectedNotification?.id)}
                            >
                                {processingIds.has(selectedNotification?.id) ? 'Rejecting...' : 'Confirm Rejection'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Attendance Feedback Modal */}
            {showAttendanceModal && (
                <div className="modal-overlay">
                    <div className="attendance-modal">
                        <div className="modalcn-header">
                            <h3>Interview Attendance</h3>
                            <button
                                className="close-button"
                                onClick={() => setShowAttendanceModal(false)}
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="modalcn-content">
                            <p>Please confirm your attendance for the interview with <strong>{selectedAttendanceNotification?.companyName}</strong>.</p>

                            <div className="attendance-selection">
                                <h4>Did you attend the interview?</h4>
                                <div className="attendance-options">
                                    <label className="attendance-option">
                                        <input
                                            type="radio"
                                            name="attendance"
                                            value="attended"
                                            checked={attendanceStatus === 'attended'}
                                            onChange={(e) => setAttendanceStatus(e.target.value)}
                                        />
                                        <span className="option-text">
                                            <Check size={16} />
                                            Yes, I attended
                                        </span>
                                    </label>

                                    <label className="attendance-option">
                                        <input
                                            type="radio"
                                            name="attendance"
                                            value="not-attended"
                                            checked={attendanceStatus === 'not-attended'}
                                            onChange={(e) => setAttendanceStatus(e.target.value)}
                                        />
                                        <span className="option-text">
                                            <X size={16} />
                                            No, I did not attend
                                        </span>
                                    </label>
                                </div>
                            </div>

                            <div className="feedback-section">
                                <h4>Feedback</h4>
                                <p>Please provide your feedback about the interview experience:</p>
                                <textarea
                                    value={feedback}
                                    onChange={(e) => setFeedback(e.target.value)}
                                    placeholder={attendanceStatus === 'attended' ?
                                        "Share your interview experience, questions asked, difficulty level, etc..." :
                                        "Please explain why you couldn't attend the interview..."
                                    }
                                    rows={4}
                                    className="feedback-textarea"
                                />
                            </div>
                        </div>

                        <div className="modalcn-actions">
                            <button
                                className="cancel-button"
                                onClick={() => setShowAttendanceModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="confirm-attendance-button"
                                onClick={submitAttendanceFeedback}
                                disabled={!attendanceStatus || !feedback.trim() || processingIds.has(selectedAttendanceNotification?.id)}
                            >
                                {processingIds.has(selectedAttendanceNotification?.id) ? 'Submitting...' : 'Submit Feedback'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompanyNotifications;